import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { PropTypes } from "prop-types";
import PerfectScrollbar from "perfect-scrollbar";
import { Nav } from "reactstrap";
import { BackgroundColorContext } from "contexts/BackgroundColorContext";
import { useTranslation } from "../../hooks/useTranslation";

var ps;

// ── Translation map ───────────────────────────────────────────────────────────
const ROUTE_TRANSLATION_MAP = {
  "/dashboard":           "navigation.dashboard",
  "/analytics":           "navigation.analytics",
  "/sales":               "navigation.sales",
  "/sales/history":       "navigation.recentSales",
  "/returns":             "navigation.returns",
  "/returns/history":     "navigation.recentReturns",
  "/exchanges":           "navigation.exchanges",
  "/exchanges/history":   "navigation.recentExchanges",
  "/orders":              "navigation.orders",
  "/orders/create":       "navigation.createOrder",
  "/customers":           "navigation.customers",
  "/catalog/products":    "navigation.products",
  "/catalog/collections": "navigation.collections",
  "/marketing/meta-ads":  "navigation.metaAds",
  "/stock-check":         "navigation.stockCheck",
  "/print-center":        "navigation.printCenter",
  "/transactions":        "navigation.transactions",
  "/cms":                 "navigation.referenceData",
  "/settings":            "navigation.administration",
  "/add/products":        "navigation.legacyIntake",
  "/products":            "navigation.legacyProducts",
  "/icons":               "navigation.icons",
  "/notifications":       "navigation.notifications",
  "/user-profile":        "navigation.userProfile",
  "/company-profile":     "navigation.companyProfile",
  "/login":               "navigation.login",
  "/tables":              "navigation.tableList",
};

function Sidebar(props) {
  const t = useTranslation();
  const location = useLocation();
  const sidebarRef = React.useRef(null);

  // ── Role-based visibility ─────────────────────────────────────────────────
  const { userRole } = props;

  const isVisible = React.useCallback(
    (route) => {
      if (!route.roles) return true;
      if (!userRole) return false;
      return route.roles.includes(userRole);
    },
    [userRole]
  );

  // ── Accordion state ───────────────────────────────────────────────────────
  const [activeDropdown, setActiveDropdown] = React.useState(null);

  // Auto-expand the parent accordion when the current URL matches the parent
  // route itself OR any of its sub-items.
  React.useEffect(() => {
    const { routes } = props;
    for (const route of routes) {
      if (!route.subItems || !route.path || !route.layout) continue;
      const parentActive = location.pathname === `${route.layout}${route.path}`;
      const childActive  = route.subItems.some(
        (sub) => location.pathname === `${sub.layout}${sub.path}`
      );
      if (parentActive || childActive) {
        setActiveDropdown(route.name);
        return;
      }
    }
    // Do not auto-collapse — user may have manually expanded a group.
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Perfect Scrollbar (Windows only) ─────────────────────────────────────
  React.useEffect(() => {
    if (navigator.platform.indexOf("Win") > -1) {
      ps = new PerfectScrollbar(sidebarRef.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1) {
        ps.destroy();
      }
    };
  });

  // ── Helpers ───────────────────────────────────────────────────────────────
  const getTranslatedName = (route) => {
    const key = ROUTE_TRANSLATION_MAP[route.path];
    if (key) return t(key);
    return props.rtlActive ? route.rtlName : route.name;
  };

  // Exact-match active check → returns "active" or ""
  const activeRoute = (routePath) =>
    location.pathname === routePath ? "active" : "";

  // Deep pages (sidebar: false) can name the item that stays highlighted
  // while they are open, e.g. a product set keeps "Meta Ads" active.
  const activeParentPath = React.useMemo(() => {
    const match = (props.routes || []).find(
      (route) =>
        route.activeParent &&
        route.layout &&
        new RegExp(`^${route.layout}${route.path.replace(/:[^/]+/g, "[^/]+")}$`).test(location.pathname)
    );
    return match ? `${match.layout}${match.activeParent}` : null;
  }, [props.routes, location.pathname]);

  // ── Logo ──────────────────────────────────────────────────────────────────
  const { routes, rtlActive, logo } = props;
  let logoImg  = null;
  let logoText = null;

  if (logo !== undefined) {
    if (logo.outterLink !== undefined) {
      logoImg = (
        <a
          href={logo.outterLink}
          className="simple-text logo-mini"
          target="_blank"
          onClick={props.toggleSidebar}
          rel="noreferrer"
        >
          <div className="logo-img">
            <img src={logo.imgSrc} alt="react-logo" />
          </div>
        </a>
      );
      logoText = (
        <a
          href={logo.outterLink}
          className="simple-text logo-normal"
          target="_blank"
          onClick={props.toggleSidebar}
          rel="noreferrer"
        >
          {logo.text}
        </a>
      );
    } else {
      logoImg = (
        <Link
          to={logo.innerLink}
          className="simple-text logo-mini"
          onClick={props.toggleSidebar}
        >
          <div className="logo-img">
            <img src={logo.imgSrc} alt="react-logo" />
          </div>
        </Link>
      );
      logoText = (
        <Link
          to={logo.innerLink}
          className="simple-text logo-normal"
          onClick={props.toggleSidebar}
        >
          {logo.text}
        </Link>
      );
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <BackgroundColorContext.Consumer>
      {({ color }) => (
        <div className="sidebar" data={color}>
          <div className="sidebar-wrapper" ref={sidebarRef}>
            {(logoImg !== null || logoText !== null) && (
              <div className="logo">
                {logoImg}
                {logoText}
              </div>
            )}

            <Nav>
              {routes.map((prop, key) => {
                // 1. Skip redirects and items explicitly hidden from sidebar
                if (prop.redirect || prop.sidebar === false) return null;

                // 2. Role-based visibility filter
                if (!isVisible(prop)) return null;

                // 3. Section label — visual divider only, no NavLink
                if (prop.sectionLabel) {
                  const sectionName = prop.labelKey
                    ? t(prop.labelKey)
                    : rtlActive
                    ? prop.rtlName || prop.name
                    : prop.name;
                  return (
                    <li key={key} className="nav-section-label">
                      <span>{sectionName}</span>
                    </li>
                  );
                }

                // ── 4a. Accordion item (has sub-items) ───────────────────────
                if (prop.subItems) {
                  const hasActiveChild = prop.subItems.some(
                    (sub) => location.pathname === `${sub.layout}${sub.path}`
                  );
                  // "active" only when the parent route itself is the current page
                  // (and no child is current — to avoid double dots).
                  // "nav-dropdown-open" signals an expanded accordion without the
                  // active-dot marker, used when a child is current or when the
                  // user manually opened the accordion.
                  const isOnParentPage =
                    !hasActiveChild &&
                    location.pathname === `${prop.layout}${prop.path}`;
                  const isExpanded = activeDropdown === prop.name;

                  const liClass = isOnParentPage
                    ? "active"
                    : isExpanded
                    ? "nav-dropdown-open"
                    : "";

                  return (
                    <li key={key} className={liClass}>
                      {/*
                        `end` prevents React Router from adding its own `active`
                        class to the <a> when a child route is current.
                      */}
                      <NavLink
                        to={prop.layout + prop.path}
                        className="nav-link"
                        end
                        onClick={() =>
                          setActiveDropdown(isExpanded ? null : prop.name)
                        }
                      >
                        <i className={prop.icon || "tim-icons icon-app"} />
                        <p>{getTranslatedName(prop)}</p>
                      </NavLink>

                      {/* Sub-items accordion panel */}
                      <Nav
                        className={
                          isExpanded ? "nav-subitems" : "nav-subitems d-none"
                        }
                      >
                        {prop.subItems.map((subProp, subKey) => {
                          const subName = ROUTE_TRANSLATION_MAP[subProp.path]
                            ? t(ROUTE_TRANSLATION_MAP[subProp.path])
                            : rtlActive
                            ? subProp.rtlName
                            : subProp.name;

                          return (
                            <li
                              key={subKey}
                              className={activeRoute(
                                `${subProp.layout}${subProp.path}`
                              )}
                            >
                              <NavLink
                                to={subProp.layout + subProp.path}
                                className="nav-link"
                                end
                                onClick={props.toggleSidebar}
                              >
                                {subProp.icon && (
                                  <i className={subProp.icon} />
                                )}
                                <p>{subName}</p>
                              </NavLink>
                            </li>
                          );
                        })}
                      </Nav>
                    </li>
                  );
                }

                // ── 4b. Regular item (no sub-items) ──────────────────────────
                // Use exact path match for the <li> active class — this ensures
                // the active dot shows correctly on direct navigation (not only
                // when clicking through the sidebar).
                const itemPath = `${prop.layout}${prop.path}`;
                return (
                  <li
                    key={key}
                    className={
                      activeParentPath === itemPath ? "active" : activeRoute(itemPath)
                    }
                  >
                    <NavLink
                      to={prop.layout + prop.path}
                      className="nav-link"
                      onClick={() => setActiveDropdown(prop.name)}
                    >
                      <i className={prop.icon || "tim-icons icon-app"} />
                      <p>{getTranslatedName(prop)}</p>
                    </NavLink>
                  </li>
                );
              })}
            </Nav>
          </div>
        </div>
      )}
    </BackgroundColorContext.Consumer>
  );
}

Sidebar.defaultProps = {
  userRole: null,
};

Sidebar.propTypes = {
  rtlActive: PropTypes.bool,
  userRole:  PropTypes.string,
  routes:    PropTypes.arrayOf(PropTypes.object),
  logo: PropTypes.shape({
    innerLink:  PropTypes.string,
    outterLink: PropTypes.string,
    text:       PropTypes.node,
    imgSrc:     PropTypes.string,
  }),
};

export default Sidebar;
